import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Alert from '../../../../shared/components/Alert/Alert';
import Loader from '../../../../shared/components/Loader/Loader';
import { getAffairDocuments, uploadAffairDocument } from '../affair.api';
import { formatAffairDate } from '../affairFormatters';

export const DOCUMENT_CATEGORIES = ['Consultation','Programme','CCTP','Plans reçus','Questions','Réponses','Compte-rendu','Offre commerciale','Commande','Contrat','Administratif','Autre'];

export default function AffairDocumentsPanel({ affair }) {
  const [documents, setDocuments] = useState([]);
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState('Consultation');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setDocuments(await getAffairDocuments(affair.id));
      setError('');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [affair.id]);

  useEffect(() => { load(); }, [load]);

  const publish = async () => {
    if (!file) return;
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      await uploadAffairDocument(affair.id, { file, category, description });
      setFile(null);
      setDescription('');
      if (inputRef.current) inputRef.current.value = '';
      await load();
      setSuccess('Le document a été publié dans la GED de l’affaire.');
    } catch (e) {
      setError(e.message || 'Le document n’a pas pu être publié.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader label="Chargement des documents..." />;

  return (
    <section className="affair-panel">
      <div className="affair-panel__header">
        <div>
          <h2>Documents de l’affaire</h2>
          <p>Déposez ici les pièces marché, données reçues et documents commerciaux. Le fichier est classé automatiquement dans la GED Nextcloud de l’affaire.</p>
        </div>
      </div>

      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}

      <div className="affair-document-form">
        <div className="custom-input custom-input--full">
          <label className="custom-input__label" htmlFor="affair-document-file">Fichier</label>
          <input
            ref={inputRef}
            id="affair-document-file"
            className="custom-input__field"
            type="file"
            onChange={(event) => setFile(event.target.files?.[0] || null)}
          />
        </div>
        <div className="custom-input">
          <label className="custom-input__label" htmlFor="affair-document-category">Catégorie</label>
          <select id="affair-document-category" className="custom-input__field" value={category} onChange={(event) => setCategory(event.target.value)}>
            {DOCUMENT_CATEGORIES.map((item) => <option key={item}>{item}</option>)}
          </select>
        </div>
        <div className="custom-input custom-input--full">
          <label className="custom-input__label" htmlFor="affair-document-description">Description</label>
          <textarea id="affair-document-description" className="custom-input__field" rows="3" value={description} onChange={(event) => setDescription(event.target.value)} />
        </div>
        <div>
          {file && <p><strong>{file.name}</strong> · {(file.size / 1024 / 1024).toFixed(2)} Mo</p>}
          <button className="basic-button active" disabled={saving || !file} type="button" onClick={publish}>
            {saving ? 'Publication…' : 'Publier dans la GED'}
          </button>
        </div>
      </div>

      <div className="affair-document-list">
        {documents.length === 0 ? <p className="affair-panel__empty">Aucun document.</p> : documents.map((document) => (
          <article key={document.id}>
            <div>
              <strong>{document.name}</strong>
              <span>{document.documentType || document.document_type || 'Autre'} · {formatAffairDate(document.createdAt || document.created_at)}</span>
            </div>
            <p>{document.description || 'Aucune description'}</p>
            <span className="affair-document-status">{document.syncStatus || document.sync_status || 'Synchronisé'}</span>
            {(document.id || document.nextcloudPath || document.nextcloud_path) && (
              <Link className="basic-button" to={`/GED?documentId=${document.id || ''}&mode=view`}>Ouvrir dans la GED</Link>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}
